import { _GettersTree, defineStore } from "pinia";
import { LessonPlanCollection } from "@/models/LessonPlanCollection.model";
import { User } from "@/models/User.model";
import { AbstractBaseStore, AbstractStoreState } from "@/stores/AbstractBase.store";
import { api } from "@/utils/Api.util";
import { useUsersStore } from "@/stores/Users.store";

export interface AuthorData {
	author: User;
	section: string; // 'colleagues' or 'publishers' or ...
	collections: LessonPlanCollection[];
	totalPlanCount: number;
}

export const authorGroupProperties: Record<string, { titleI18nKey: string; emptyMessageI18nKey: string; accentColor: string }> = {
	'colleagues': {
		titleI18nKey: 'Colleague',
		emptyMessageI18nKey: 'No colleagues sharing their lesson plans yet.',
		accentColor: 'accent-focus',
	},
	'publishers': {
		titleI18nKey: 'Publisher',
		emptyMessageI18nKey: 'No shared collections found',
		accentColor: 'accent2-focus',
	},
};

interface DiscoverCollectionsStoreState extends AbstractStoreState<LessonPlanCollection, any> {}

const BaseStore = AbstractBaseStore<LessonPlanCollection, DiscoverCollectionsStoreState, any>();

const forks = new Map<string, any>();

/**
 * Create a discover collections store fork for a specific endpoint.
 *
 * @param name - Unique Pinia store name
 * @param endpoint - The discover endpoint suffix ('colleagues' or 'public')
 */
export function forkDiscoverCollectionsStore(name: string, endpoint: string) {

	if (!forks.has(name)) {
		forks.set(name, defineStore(name, {

			state: (): DiscoverCollectionsStoreState => {
				return BaseStore.state({});
			},

			getters: {
				...BaseStore.getters,

				apiClient() {
					return api;
				},

				apiPath() {
					return 'users/' + useUsersStore().getMe().getId() + '/discover/' + endpoint;
				},

				defaultMask(): string[] {
					return [
						"id",
						"name",
						"planCount",
						"sharing",
						"owner.*",
						"owner.planCount",
					];
				},

				defaultRecordsPerPage(): number {
					return 1000;
				},

				/**
				 * Group collections by author.
				 */
				groupedByAuthor(state): AuthorData[] {
					const map = new Map<string, AuthorData>();
					for (const collection of state.items) {
						if (!collection.owner) continue;
						const ownerId = collection.owner.id;
						if (!map.has(ownerId)) {
							map.set(ownerId, {
								author: collection.owner,
								section: endpoint,
								collections: [],
								totalPlanCount: collection.owner.planCount,
							});
						}
						const group = map.get(ownerId)!;
						group.collections.push(collection);
					}
					return Array.from(map.values());
				},

			} as _GettersTree<DiscoverCollectionsStoreState>,

			actions: {
				...BaseStore.actions,

				mapToItem(data: any): LessonPlanCollection {
					return LessonPlanCollection.createFromApiData(data);
				},
			},

		}));
	}

	return forks.get(name);
}

/**
 * Discover shared collections from colleagues (same account or school group).
 */
export const useLessonPlanCollectionsFromColleaguesStore = forkDiscoverCollectionsStore('lessonPlanCollectionsFromColleagues', 'colleagues');

/**
 * Discover publicly shared collections from verified authors/publishers.
 */
export const usePublicLessonPlanCollectionsStore = forkDiscoverCollectionsStore('publicLessonPlanCollections', 'public');

/**
 * just for setting context in library and agenda views without affecting the main discover stores
 */
export const useDiscoverStateStore = defineStore('discoverState', {
    state: () => ({
        lessonPlanIdsInBlock: [] as string[],
    }),
    actions: {
        setLessonPlanIdsInBlock(ids: string[]) {
            this.lessonPlanIdsInBlock = ids;
        },
    }
});
